import React, { useEffect, useMemo, useCallback } from "react";
import { Button } from "@material-ui/core";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AiOutlineArrowRight } from "react-icons/ai";
import { getAllOrdersOfUser } from "../../redux/actions/order";
import { useNotifications } from "../../hooks/useNotifications";
import DataGridWrapper from "./DataGridWrapper";
import {
  orderBelongsInBuyerDisputesList,
  getOrderListStatusLabel,
  isOrderListStatusSuccess,
  isOrderListStatusResolvedNoRefund,
  orderIssueReportedOnly,
} from "../../utils/orderListStatus";
import {
  BUYER_REFUND_UPDATE_TYPES,
  VA_MEDIATION_NOTIFICATION_TYPES,
} from "../../utils/notificationTypes";
import {
  getNotificationRelatedOrderId,
  isBuyerFacingRefundNotification,
} from "../../utils/orderNotificationMatch";
import { primeMobileDashboardContentScroll } from "../../utils/navigateWithMobileDashboardScroll";

const formatCurrency = (value) => {
  if (value === null || value === undefined) return "£0.00";
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(Number(value));
};

const BUYER_DISPUTE_NOTIF_TYPES = new Set([
  ...BUYER_REFUND_UPDATE_TYPES,
  ...VA_MEDIATION_NOTIFICATION_TYPES,
]);

const AllRefundOrdersBuyer = React.memo(({ cardClass }) => {
  const { user } = useSelector((state) => state.user);
  const { orders } = useSelector((state) => state.order);
  const dispatch = useDispatch();
  const userId = user?._id;
  const { notifications } = useNotifications();

  useEffect(() => {
    if (userId) {
      dispatch(getAllOrdersOfUser(userId));
    }
  }, [dispatch, userId]);

  const notificationMeta = useMemo(() => {
    const counts = {};
    const titles = {};
    if (!Array.isArray(notifications)) return { counts, titles };
    notifications.forEach((notif) => {
      if (notif.read || !BUYER_DISPUTE_NOTIF_TYPES.has(notif.type)) return;
      if (!isBuyerFacingRefundNotification(notif)) return;
      const orderId = getNotificationRelatedOrderId(notif);
      if (!orderId) return;
      counts[orderId] = (counts[orderId] || 0) + 1;
      if (!titles[orderId]) {
        titles[orderId] = notif.title || notif.message || "Refund update";
      }
    });
    return { counts, titles };
  }, [notifications]);

  const eligibleOrders = useMemo(() => {
    if (!Array.isArray(orders)) return [];
    const byId = new Map();
    orders.forEach((order) => {
      if (orderBelongsInBuyerDisputesList(order)) {
        byId.set(String(order._id), order);
      }
    });
    // Always surface purchase orders that have unread buyer refund alerts,
    // even if status filtering missed them.
    Object.keys(notificationMeta.counts).forEach((orderId) => {
      if (byId.has(orderId)) return;
      const order = orders.find((o) => String(o._id) === orderId);
      if (order) byId.set(orderId, order);
    });
    return [...byId.values()];
  }, [orders, notificationMeta.counts]);

  const columns = useMemo(
    () => [
      {
        field: "orderIdDisplay",
        headerName: "Order ID",
        minWidth: 180,
        flex: 0.9,
        renderCell: (params) => {
          const rowId = params.id?.toString?.() || "";
          const unreadCount =
            Number(notificationMeta.counts[rowId]) ||
            Number(params.row?.unreadCount) ||
            0;
          const hasUnread = unreadCount > 0;
          return (
            <div
              className={`flex min-w-0 items-center gap-1.5 ${
                hasUnread ? "rounded-md bg-red-50 px-1.5 py-1" : ""
              }`}
            >
              {hasUnread ? (
                <span className="inline-flex shrink-0 items-center rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                  New
                </span>
              ) : null}
              <span
                className={`font-semibold ${
                  hasUnread ? "text-red-700" : "text-slate-800"
                }`}
              >
                #{params.row.orderIdDisplay}
              </span>
            </div>
          );
        },
      },
      {
        field: "status",
        headerName: "Status",
        minWidth: 150,
        flex: 0.7,
        renderCell: (params) => {
          const statusLabel = params.row.statusLabel || params.value;
          const rowId = params.id?.toString?.() || "";
          const unreadCount =
            Number(notificationMeta.counts[rowId]) ||
            Number(params.row?.unreadCount) ||
            0;
          const isGreen = isOrderListStatusSuccess(statusLabel);
          const isResolved = isOrderListStatusResolvedNoRefund(statusLabel);
          const colorClass = isGreen ? "greenColor" : isResolved ? "blueColor" : "redColor";
          return (
            <div className="flex items-center gap-2">
              <span className={colorClass}>{statusLabel}</span>
              {unreadCount > 0 && (
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs font-semibold text-white">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </div>
          );
        },
      },
      {
        field: "itemsQty",
        headerName: "Items qty",
        type: "number",
        minWidth: 110,
        flex: 0.4,
      },
      {
        field: "total",
        headerName: "Total",
        type: "number",
        minWidth: 120,
        flex: 0.5,
      },
      {
        field: "actions",
        flex: 0.4,
        minWidth: 100,
        headerName: "",
        sortable: false,
        renderCell: (params) => (
          <Link
            to={`/profile/order/${params.id}`}
            onClick={(e) => {
              e.stopPropagation();
              primeMobileDashboardContentScroll(`/profile/order/${params.id}`);
            }}
          >
            <Button>
              <AiOutlineArrowRight size={18} />
            </Button>
          </Link>
        ),
      },
    ],
    [notificationMeta.counts]
  );

  const rows = useMemo(() => {
    const sorted = [...eligibleOrders].sort((a, b) => {
      const aUnread = notificationMeta.counts[a._id?.toString()] || 0;
      const bUnread = notificationMeta.counts[b._id?.toString()] || 0;
      if (bUnread !== aUnread) return bUnread - aUnread;
      const ta = new Date(a?.refundRequestedAt || a?.createdAt || 0).getTime();
      const tb = new Date(b?.refundRequestedAt || b?.createdAt || 0).getTime();
      return tb - ta;
    });

    return sorted.map((item) => {
      const id = item._id?.toString();
      const unreadCount = notificationMeta.counts[id] || 0;
      return {
        id: item._id,
        orderIdDisplay: item?.orderNumber ?? item?._id?.toString().substring(0, 8) ?? "N/A",
        itemsQty: item.cart?.length ?? 0,
        total: formatCurrency(item.totalPrice || 0),
        status: item.status,
        statusLabel: orderIssueReportedOnly(item)
          ? "Issue reported"
          : getOrderListStatusLabel(item),
        unreadCount,
        unreadLabel: notificationMeta.titles[id] || "",
      };
    });
  }, [eligibleOrders, notificationMeta]);

  const unreadRows = useMemo(
    () => rows.filter((r) => (r.unreadCount || 0) > 0),
    [rows]
  );

  const handleRowClick = useCallback((params, navigate) => {
    navigate(`/profile/order/${params.id}`);
  }, []);

  const description =
    unreadRows.length > 0
      ? `Monitor your refund requests and disputes. ${unreadRows.length} marked New — open them for the latest update.`
      : "Monitor the status of your refund requests and disputes.";

  return (
    <div className="space-y-3">
      {unreadRows.length > 0 ? (
        <div
          className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-950"
          role="status"
        >
          <p className="font-semibold">
            {unreadRows.length === 1
              ? "1 refund has a new update"
              : `${unreadRows.length} refunds have new updates`}
          </p>
          <ul className="mt-2 flex flex-col gap-1.5">
            {unreadRows.slice(0, 8).map((row) => (
              <li key={String(row.id)}>
                <Link
                  to={`/profile/order/${row.id}`}
                  onClick={() =>
                    primeMobileDashboardContentScroll(`/profile/order/${row.id}`)
                  }
                  className="inline-flex items-center gap-2 font-semibold text-red-700 underline hover:text-red-900"
                >
                  <span className="rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-bold uppercase text-white">
                    New
                  </span>
                  Order #{row.orderIdDisplay}
                  {row.unreadLabel ? (
                    <span className="font-normal text-red-800/80">— {row.unreadLabel}</span>
                  ) : null}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <DataGridWrapper
        title="My Refunds"
        description={description}
        rows={rows}
        columns={columns}
        cardClass={cardClass}
        emptyMessage="No refund requests or disputes yet. If you sell and have buyer requests to review, open the Buyer Refunds tab."
        onRowClick={handleRowClick}
        getRowClassName={(params) =>
          (params.row?.unreadCount || 0) > 0 ? "order-row-has-update" : ""
        }
      />
    </div>
  );
});

AllRefundOrdersBuyer.displayName = "AllRefundOrdersBuyer";

export default AllRefundOrdersBuyer;
